import React from "react";
import ShatterText from "../../components/ShatterText";
import FlashText from "../../components/FlashText";
import ParallaxLayer from "../../components/ParallaxLayer";
import type { CardProps, HeroProps, SectionHeaderProps } from "../types";
import { GLYPH_BOX_HEIGHT, layoutFragments } from "./letterforms";
import "./alien.css";

/**
 * ALIEN — the used future.
 *
 * Retro-industrial: worn, functional, no gloss. The ship's computer is the
 * interface, so headings are bracketed stations and cards are manifest rows.
 */

const PAD = 12;

const REVEAL_MS = 2500;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The fragment-assembly title. Stroke fragments fade in scattered and out of
 * order over ~2.5s (the film takes ~40s), so the word resolves in your head
 * before the animation finishes.
 */
export const AlienHero: React.FC<HeroProps> = ({ name, tagline }) => {
  const { fragments, width } = React.useMemo(
    () => layoutFragments(name),
    [name]
  );

  // Delays are derived from the index rather than random so a re-render never
  // restarts a fragment mid-flight.
  const delays = React.useMemo(
    () =>
      fragments.map((_, i) => {
        // A deterministic hash scatters the order without Math.random.
        const scattered = ((i * 2654435761) % 1000) / 1000;
        return scattered * REVEAL_MS * 0.72;
      }),
    [fragments]
  );

  const reduce = prefersReducedMotion();

  return (
    <>
      <h1 className="hero-title al-hero-title" aria-label={name}>
        <svg
          className="al-title-svg"
          viewBox={`${-PAD} ${-PAD} ${width + PAD * 2} ${
            GLYPH_BOX_HEIGHT + PAD * 2
          }`}
          role="img"
          aria-label={name}
        >
          {fragments.map((f, i) => (
            <line
              key={i}
              x1={f.x1}
              y1={f.y1}
              x2={f.x2}
              y2={f.y2}
              className={reduce ? "al-frag al-frag--static" : "al-frag"}
              style={reduce ? undefined : { animationDelay: `${delays[i]}ms` }}
            />
          ))}
        </svg>
      </h1>
      <p className="hero-subtitle al-tagline">{tagline}</p>
    </>
  );
};

/** Sections are ship stations: bracketed, tracked out, with a numeric code. */
export const AlienSectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  index,
}) => (
  <h2 className="section-heading al-section-heading">
    <span className="al-bracket">[</span>
    <span className="al-section-label">
      <ShatterText text={title} />
    </span>
    <span className="al-bracket">]</span>
    <span className="al-section-code">
      {String((index + 1) * 7).padStart(2, "0")}-
      {String(title.length * 113).padStart(4, "0")}
    </span>
  </h2>
);

/** Cards are cargo-manifest rows: corner brackets, label/value pairs. */
export const AlienCard: React.FC<CardProps> = ({ item, index }) => (
  <div className="item al-card">
    <span className="al-corner al-corner--tl" aria-hidden="true" />
    <span className="al-corner al-corner--tr" aria-hidden="true" />
    <span className="al-corner al-corner--bl" aria-hidden="true" />
    <span className="al-corner al-corner--br" aria-hidden="true" />

    <div className="al-card-index">
      {String(index + 1).padStart(3, "0")}
    </div>

    <div className="al-card-body">
      <h3 className="item-title al-card-title">
        {item.href ? (
          <a
            className="item-title-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ShatterText text={item.title} />
          </a>
        ) : (
          <ShatterText text={item.title} />
        )}
      </h3>

      <dl className="al-manifest">
        {item.subtitle && (
          <>
            <dt>Designation</dt>
            <dd>
              <ShatterText text={item.subtitle} />
            </dd>
          </>
        )}
        {item.meta && (
          <>
            <dt>Log</dt>
            <dd>
              <ShatterText text={item.meta} />
            </dd>
          </>
        )}
      </dl>

      <p className="item-description al-card-description">
        <FlashText text={item.description} />
      </p>

      {item.action && (
        <a
          href={item.action.href}
          target="_blank"
          rel="noopener noreferrer"
          className="item-link al-card-action"
        >
          {item.action.label}
        </a>
      )}
    </div>
  </div>
);

type Contact = {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  duration: number;
};

const TRACKER_RADIUS = 95;

/**
 * Motion tracker: concentric rings, a rotating sweep, and contacts.
 *
 * Contacts appear at random bearings and drift inward at varied speeds rather
 * than a single dot looping the same path — the tracker should feel like it is
 * reading a room, not playing an animation.
 */
export const AlienBackdrop: React.FC = () => {
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const nextId = React.useRef(0);

  React.useEffect(() => {
    if (prefersReducedMotion()) return;

    let timer = 0;

    const spawn = () => {
      const id = nextId.current++;
      const bearing = Math.random() * Math.PI * 2;
      // Start out near the rim, end somewhere closer in — but rarely dead centre.
      const fromR = TRACKER_RADIUS * (0.62 + Math.random() * 0.34);
      const toR = TRACKER_RADIUS * (0.05 + Math.random() * 0.4);
      // A slight bearing drift keeps tracks from being perfectly radial.
      const drift = (Math.random() - 0.5) * 0.9;
      const duration = 3200 + Math.random() * 5200;

      const contact: Contact = {
        id,
        fromX: Math.cos(bearing) * fromR,
        fromY: Math.sin(bearing) * fromR,
        toX: Math.cos(bearing + drift) * toR,
        toY: Math.sin(bearing + drift) * toR,
        duration,
      };

      setContacts((current) => [...current, contact]);
      window.setTimeout(
        () => setContacts((current) => current.filter((c) => c.id !== id)),
        duration
      );

      timer = window.setTimeout(spawn, 700 + Math.random() * 2100);
    };

    timer = window.setTimeout(spawn, 400);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <ParallaxLayer
        className="al-ship"
        factor={0.035}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/refs/alien-backdrop.jpg)`,
        }}
      />
      <div className="al-ship-scrim" aria-hidden="true" />

      <div className="al-tracker" aria-hidden="true">
      <div className="al-tracker-ring al-tracker-ring--1" />
      <div className="al-tracker-ring al-tracker-ring--2" />
      <div className="al-tracker-ring al-tracker-ring--3" />
      <div className="al-tracker-crosshair" />
      <div className="al-tracker-sweep" />

      {contacts.map((c) => (
        <span
          key={c.id}
          className="al-contact"
          style={
            {
              "--al-fx": `${c.fromX}px`,
              "--al-fy": `${c.fromY}px`,
              "--al-tx": `${c.toX}px`,
              "--al-ty": `${c.toY}px`,
              animationDuration: `${c.duration}ms`,
            } as React.CSSProperties
          }
        />
        ))}
      </div>
    </>
  );
};

/** Scanlines plus fixed corner readouts, the way every Nostromo screen has. */
export const AlienChrome: React.FC = () => (
  <>
    {/* Panel seams. The Nostromo is a working ship, so the frame is broken up
        by structure rather than being an even wash of dark. */}
    <div className="al-plating" aria-hidden="true" />
    <div className="al-scanlines" aria-hidden="true" />
    <div className="al-hud al-hud--tl" aria-hidden="true">
      NOSTROMO 180924609
    </div>
    <div className="al-hud al-hud--tr" aria-hidden="true">
      SYS · NOMINAL
    </div>
    <div className="al-hud al-hud--bl" aria-hidden="true">
      CREW 7 · SCI 1
    </div>
  </>
);
